import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { MarketingPage } from '../routes';
import { useContent, type SiteContent } from '../siteContent';

export function ArrowMark({ className = 'mx-arrow' }: { className?: string }) {
  // The Mealnex arrow, drawn as a small inline glyph for buttons and links.
  return <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3.5h7.5V11" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /><path d="M12.2 3.8 3.6 12.4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /></svg>;
}

export function Logo({ href = '/' }: { href?: string }) {
  return <a href={href} className="mx-logo">
    <img src="/site/mark-96.png" width="34" height="34" alt="" />
    <span className="mx-logo-word"><b>ميلنكس</b><small>MEALNEX</small></span>
  </a>;
}

export function ButtonLink({ href, children, variant = 'primary', arrow = false, className = '' }: { href: string; children: ReactNode; variant?: 'primary' | 'quiet' | 'ghost' | 'light'; arrow?: boolean; className?: string }) {
  return <a href={href} className={`mx-btn mx-btn-${variant} ${className}`.trim()}>{children}{arrow && <ArrowMark />}</a>;
}

function homeLinks(content: SiteContent) {
  const { nav } = content;
  return [
    { id: 'system', label: nav.system, on: content.system.visible },
    { id: 'features', label: nav.features, on: content.features.visible },
    { id: 'journey', label: nav.journey, on: content.journey.visible },
    { id: 'pricing', label: nav.pricing, on: content.pricing.visible },
    { id: 'faq', label: nav.faq, on: content.faq.visible },
  ].filter(link => link.on && link.label);
}

const waLink = (value: string) => `https://wa.me/${value.replace(/[^\d]/g, '').replace(/^0/, '966')}`;

function Announcement() {
  const { announcement } = useContent();
  if (!announcement.enabled || !announcement.text) return null;
  return <div className="mx-announce"><div className="mx-container"><span>{announcement.text}</span>{announcement.linkUrl && <a href={announcement.linkUrl}>{announcement.linkLabel || 'التفاصيل'}<ArrowMark /></a>}</div></div>;
}

export function SiteHeader({ page }: { page: MarketingPage }) {
  const content = useContent();
  const { nav, settings } = content;
  const links = homeLinks(content);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prefix = page === 'home' ? '' : '/';

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); toggleRef.current?.focus(); }
      if (event.key === 'Tab' && panelRef.current) {
        const items = [toggleRef.current, ...panelRef.current.querySelectorAll<HTMLElement>('a')].filter(Boolean) as HTMLElement[];
        const first = items[0]; const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    const onResize = () => { if (window.innerWidth > 960) setOpen(false); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => { document.body.style.overflow = previous; document.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize); };
  }, [open]);

  return <header className={`mx-header${scrolled || open ? ' is-scrolled' : ''}${open ? ' is-open' : ''}`}>
    <div className="mx-header-bar mx-container">
      <Logo />
      <nav className="mx-nav" aria-label="التنقل الرئيسي">
        {links.map(link => <a key={link.id} href={`${prefix}#${link.id}`}>{link.label}</a>)}
      </nav>
      <div className="mx-header-actions">
        {nav.login && <a className="mx-link-login" href={settings.loginUrl}>{nav.login}</a>}
        <ButtonLink href={settings.primaryCta} className="mx-btn-sm">{nav.cta}</ButtonLink>
        <button ref={toggleRef} type="button" className="mx-menu-toggle" aria-expanded={open} aria-controls={menuId} aria-label={open ? 'إغلاق القائمة' : 'فتح القائمة'} onClick={() => setOpen(value => !value)}>
          <span /><span />
        </button>
      </div>
    </div>
    <div id={menuId} ref={panelRef} className="mx-mobile-menu" hidden={!open}>
      <nav aria-label="قائمة الجوال" className="mx-container">
        {links.map(link => <a key={link.id} href={`${prefix}#${link.id}`} onClick={() => setOpen(false)}>{link.label}</a>)}
        <div className="mx-mobile-menu-actions">
          <ButtonLink href={settings.primaryCta} arrow>{nav.cta}</ButtonLink>
          {nav.login && <ButtonLink href={settings.loginUrl} variant="quiet">{nav.login}</ButtonLink>}
        </div>
      </nav>
    </div>
  </header>;
}

export function SiteFooter({ page }: { page: MarketingPage }) {
  const content = useContent();
  const { settings, nav } = content;
  const prefix = page === 'home' ? '' : '/';
  return <footer className="mx-footer">
    <div className="mx-container mx-footer-grid">
      <div className="mx-footer-brand">
        <Logo />
        <p>{settings.footerText}</p>
      </div>
      <nav aria-label="النظام">
        <h2>النظام</h2>
        {homeLinks(content).filter(link => link.id !== 'faq').map(link => <a key={link.id} href={`${prefix}#${link.id}`}>{link.label}</a>)}
        <a href={settings.primaryCta}>اطلب النظام</a>
      </nav>
      <nav aria-label="الدعم">
        <h2>الدعم</h2>
        <a href="/support">مركز الدعم</a>
        <a href={`mailto:${settings.supportEmail}`}>تواصل معنا</a>
        {settings.phone && <a href={`tel:${settings.phone.replace(/\s/g, '')}`} dir="ltr">{settings.phone}</a>}
        {settings.whatsapp && <a href={waLink(settings.whatsapp)} rel="noopener" target="_blank">واتساب</a>}
        {content.faq.visible && <a href={`${prefix}#faq`}>{nav.faq}</a>}
      </nav>
      <nav aria-label="القانونية">
        <h2>القانونية</h2>
        <a href="/privacy">سياسة الخصوصية</a>
        <a href="/terms">الشروط والأحكام</a>
        <a href="/terms#subscriptions">سياسة الاشتراكات والإلغاء</a>
      </nav>
    </div>
    <div className="mx-container mx-footer-base">
      <small>{settings.copyright}</small>
      <a href={settings.loginUrl}>دخول المطاعم</a>
    </div>
  </footer>;
}

export function SiteFrame({ page, children }: { page: MarketingPage; children: ReactNode }) {
  return <div className="mx-site">
    <a className="mx-skip" href="#main">تخطَّ إلى المحتوى</a>
    <Announcement />
    <SiteHeader page={page} />
    <main id="main" tabIndex={-1}>{children}</main>
    <SiteFooter page={page} />
  </div>;
}
